// MaterialCreate — the "New material" creation dialog CONTENT, pure.
// Composes RadioGroup, Field, Input, Select, Button, Text, Spinner.
//
// The real creation flow the master audit's R4 demands (ui-master-audit §4):
// the author picks a template (the catalogue arrives from `GET /templates`
// via the host), names the material, and optionally grounds it in an
// existing source — then the host submits the ONE existing `createMaterial`
// command and lands the author in the editor. This replaces the old
// mislabeled path where "New material" opened the editor's block palette
// and silently mutated (or no-oped on) whatever was open.
//
// Pure/props-driven like ExportFlow: the submission PROGRESS is a sum
// (`MaterialCreateState`, one variant per moment) arriving as a prop, and
// every transition is reported UP via `onCreate`. What the component holds
// is ephemeral form state only (highlighted template, draft title, picked
// source) — committed upward exactly once, on Create.

import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { useState } from "react";
import { Button } from "../Button/Button.js";
import { Field } from "../Field/Field.js";
import { HelpHint } from "../HelpHint/HelpHint.js";
import { InlineError } from "../InlineError/InlineError.js";
import { Input } from "../Input/Input.js";
import { RadioGroup, type RadioGroupOption } from "../RadioGroup/RadioGroup.js";
import { Select, type SelectOption } from "../Select/Select.js";
import { Spinner } from "../Spinner/Spinner.js";
import { Text } from "../Text/Text.js";
import "./MaterialCreate.css";

// One pickable template, as `GET /templates` lists it (id + author-facing
// title). The port speaks this surface type — no parallel shape.
export type TemplateChoice = {
    readonly id: string;
    readonly title: string;
};

// One pickable source from the active project's library.
export type SourceChoice = {
    readonly id: string;
    readonly title: string;
};

// The template catalogue's arrival state — a sum, so "still fetching" and
// "the fetch failed" render honestly instead of as an empty picker.
export type TemplateCatalog =
    | { readonly type: "loading" }
    | {
          readonly type: "loaded";
          readonly templates: readonly TemplateChoice[];
      }
    | { readonly type: "unavailable" };

// What the author asked to create. `fromSourceId` is absent when no source
// was picked (an ungrounded material) — the host maps this 1:1 onto the
// `createMaterial` command body.
export type NewMaterial = {
    readonly templateId: string;
    readonly title: string;
    readonly fromSourceId?: string;
};

// The submission's progress as a sum — exactly one moment at a time.
//   idle     → the author is filling the form.
//   creating → the command is in flight (form locked).
//   failed   → the command refused; `message` is the author-facing reason.
//              The form stays editable for another attempt.
export type MaterialCreateState =
    | { readonly type: "idle" }
    | { readonly type: "creating" }
    | { readonly type: "failed"; readonly message: string };

export type MaterialCreateProps = {
    readonly catalog: TemplateCatalog;
    // The active project's sources; empty hides the optional source picker
    // (nothing to ground in — progressive disclosure, not a dead control).
    readonly sources: readonly SourceChoice[];
    readonly state: MaterialCreateState;
    // Optional seed for the title field (P17): the palette's Enter-on-zero
    // "Create material '<query>'" pre-fills the typed query. Absent → empty.
    // Seeds the initial value only; the field stays editable.
    readonly initialTitle?: string;
    readonly onCreate: (material: NewMaterial) => void;
};

// The Select sentinel for "no source picked" — Radix forbids an empty item
// value, and real ids are uuids/prefixed slugs, so this cannot collide.
const NO_SOURCE = "__none__";

export const MaterialCreate = ({
    catalog,
    sources,
    state,
    initialTitle = "",
    onCreate,
}: MaterialCreateProps): ReactNode => {
    const { t } = useLingui();
    // Ephemeral form state — never leaves the component until Create
    // reports it up (the ExportFlow precedent).
    const [templateId, setTemplateId] = useState("");
    const [title, setTitle] = useState(initialTitle);
    // Has the author committed to a title of their own? An explicit host seed
    // counts as intent (don't clobber it); an empty start does not. While
    // untouched, the title MIRRORS the picked source (auto-name from source —
    // the source is the material's subject; smart-defaults.md #4). The first
    // keystroke in the title field flips this true, so a typed name is never
    // overwritten by a later source change.
    const [titleTouched, setTitleTouched] = useState(initialTitle !== "");
    const [sourceId, setSourceId] = useState(NO_SOURCE);

    // Picking a source seeds the title from that source's title, unless the
    // author has already typed one. "No source" leaves the title untouched
    // (never wipes what's there). The title still submits as a concrete value.
    const pickSource = (nextSourceId: string) => {
        setSourceId(nextSourceId);
        if (titleTouched) return;
        const picked = sources.find((s) => s.id === nextSourceId);
        if (picked) setTitle(picked.title);
    };

    const templates = catalog.type === "loaded" ? catalog.templates : [];
    // Guard against a not-yet-made (or stale) highlight: default to the
    // catalogue's first template.
    const activeTemplate = templates.some((tp) => tp.id === templateId)
        ? templateId
        : (templates[0]?.id ?? "");
    const creating = state.type === "creating";
    const canCreate = activeTemplate !== "" && title.trim() !== "" && !creating;

    // The catalogue's `title` is a core-supplied ENGLISH string — rendering it
    // verbatim leaked English into the Swedish dialog (H8, coverage-rule
    // regression). Localise by the stable template id; an unknown id keeps the
    // server title (forward-compat) rather than showing a blank.
    const templateLabel = (tp: TemplateChoice): string => {
        switch (tp.id) {
            case "T_freeform":
                return t`Freeform — no fixed shape`;
            case "T_quiz":
                return t`Quiz — multiple choice`;
            case "T_exercise":
                return t`Exercise — open response`;
            default:
                return tp.title;
        }
    };
    // Freeform is the one template whose absence of structure needs a second
    // sentence (H8's hint half, catalog row): the label names it, the hint
    // explains what "no fixed shape" means for the assistant's behaviour. The
    // hint sits inside the RadioGroup's own <label> — safe, because a click on
    // an interactive descendant (this button) never forwards to the radio
    // (HTML label activation behaviour excludes interactive content).
    const templateOptionLabel = (tp: TemplateChoice): ReactNode => {
        const label = templateLabel(tp);
        if (tp.id !== "T_freeform") return label;
        return (
            <span className="bilda-material-create__template-label">
                {label}
                <HelpHint label={t`Explanation: freeform`}>
                    <Trans>
                        Free-form material with no fixed structure – the
                        assistant follows no question-and-answer template.
                    </Trans>
                </HelpHint>
            </span>
        );
    };
    const templateOptions: readonly RadioGroupOption[] = templates.map(
        (tp): RadioGroupOption => ({
            value: tp.id,
            label: templateOptionLabel(tp),
        }),
    );
    const sourceOptions: readonly SelectOption[] = [
        { value: NO_SOURCE, label: t`No source` },
        ...sources.map((s): SelectOption => ({ value: s.id, label: s.title })),
    ];

    const submit = () => {
        if (!canCreate) return;
        onCreate({
            templateId: activeTemplate,
            title: title.trim(),
            ...(sourceId === NO_SOURCE ? {} : { fromSourceId: sourceId }),
        });
    };

    return (
        <form
            className="bilda-material-create"
            onSubmit={(e) => {
                e.preventDefault();
                submit();
            }}
        >
            {catalog.type === "loading" ? (
                <Spinner label={t`Loading templates…`} />
            ) : catalog.type === "unavailable" ? (
                <Text as="p" tone="dim">
                    <Trans>
                        Couldn't load the template catalogue. Close this dialog
                        and try again.
                    </Trans>
                </Text>
            ) : (
                <div className="bilda-material-create__templates">
                    <Text
                        as="div"
                        size="sm"
                        weight="medium"
                        className="bilda-material-create__label"
                    >
                        <Trans>Template</Trans>
                    </Text>
                    <RadioGroup
                        options={templateOptions}
                        value={activeTemplate}
                        onValueChange={setTemplateId}
                        name="material-template"
                        ariaLabel={t`Template`}
                        disabled={creating}
                    />
                </div>
            )}

            <Field label={t`Title`} htmlFor="material-create-title" required>
                <Input
                    id="material-create-title"
                    value={title}
                    onChange={(e) => {
                        setTitle(e.target.value);
                        setTitleTouched(true);
                    }}
                    placeholder={t`Name your material`}
                    disabled={creating}
                    // Keyboard-first: focus the primary field on open, and
                    // select any prefilled title so the first keystroke
                    // REPLACES it (accept-by-Enter, overwrite-by-typing).
                    autoFocus
                    onFocus={(e) => e.currentTarget.select()}
                />
            </Field>

            {sources.length > 0 ? (
                <div className="bilda-material-create__source">
                    <Text
                        as="div"
                        size="sm"
                        weight="medium"
                        className="bilda-material-create__label"
                    >
                        <Trans>From source</Trans>
                    </Text>
                    <Select
                        options={sourceOptions}
                        value={sourceId}
                        onValueChange={pickSource}
                        ariaLabel={t`From source`}
                        disabled={creating}
                    />
                    <Text as="p" size="xs" tone="dim">
                        <Trans>
                            Optional — grounds the material in a source you've
                            added.
                        </Trans>
                    </Text>
                </div>
            ) : null}

            {state.type === "failed" ? (
                <InlineError>{state.message}</InlineError>
            ) : null}

            <div className="bilda-material-create__actions">
                <Button type="submit" variant="primary" disabled={!canCreate}>
                    {creating ? (
                        <Trans>Creating…</Trans>
                    ) : (
                        <Trans>Create material</Trans>
                    )}
                </Button>
            </div>
        </form>
    );
};
